import { api } from "../api";
import { generateLessonIcs } from "../utils/calendar-export";
import {
  WEEKDAY_NAMES, isoDate, addDays, mondayOf, isoWeek, formatDate,
} from "../utils/date-utils";
import { showToast } from "../utils/toast";
import type { StudentTimetable, StudentLesson, StudentClosedDay } from "../types";
import { esc } from "../utils/html";

/**
 * Mein Stundenplan im Schüler-Portal.
 *
 * Bewusst keine Gitteransicht wie in der Schulverwaltung: das Raster dort braucht rund
 * 860 Pixel Breite und ließe sich auf einem Telefon nur seitwärts scrollend lesen.
 * Stattdessen eine nach Tagen gruppierte Liste, die von 360 Pixeln bis Desktop ohne
 * zweite Render-Variante funktioniert.
 */

/** Montag der angezeigten Woche. */
let weekStart = mondayOf(new Date());
let data: StudentTimetable | null = null;
let currentEl: HTMLElement | null = null;

/** Wie weit der Kalenderexport in die Zukunft reicht. Eine Woche wäre nutzlos. */
const EXPORT_DAYS = 182;

function weekEnd(): Date { return addDays(weekStart, 6); }

/** Gilt an diesem Tag ein unterrichtsfreier Zeitraum? Liefert dessen Namen. */
function closureFor(iso: string, closedDays: StudentClosedDay[]): string | null {
  const hit = closedDays.find(c => iso >= c.startDate && iso <= c.endDate);
  return hit ? hit.name : null;
}

function renderLesson(l: StudentLesson): string {
  const cancelled = l.status === "entfall";
  // Bei einer Vertretung stehen in lecturerName und room bereits die tatsächlichen
  // Werte; das Kennzeichen sagt nur, dass es vom ursprünglichen Plan abweicht.
  const meta = cancelled ? "" : [l.lecturerName, l.room].filter(Boolean).join(" · ");
  // Gleiches Badge wie in der Praktikumsplanung, damit derselbe Zustand überall
  // gleich aussieht. Das Auge für "nicht sichtbar" gibt es hier nicht: solche
  // Termine erreichen das Portal gar nicht.
  const preliminary = l.isPreliminary
    ? `<span class="ab-preliminary" title="Vorläufig">Vorläufig</span>`
    : "";
  // Eine ausgefallene Stunde bleibt stehen, damit sie als Ausfall zu sehen ist. Würde
  // sie verschwinden, sähe die Lücke aus wie ein Tag ohne geplanten Unterricht.
  const badge = cancelled
    ? `<span class="ab-cancelled" title="Fällt aus">Entfällt</span>`
    : l.status === "vertretung"
      ? `<span class="ab-substitute" title="Vertretung">Vertretung</span>`
      : "";
  return `
    <div class="st-lesson${cancelled ? " st-lesson-cancelled" : ""}">
      <span class="st-lesson-bar" style="background:${esc(l.subjectColor)}"></span>
      <span class="st-lesson-time">${esc(l.startTime)}<br>${esc(l.endTime)}</span>
      <span class="st-lesson-main">
        <span class="st-lesson-name">${esc(l.subjectName)}${badge}${preliminary}</span>
        ${meta ? `<span class="st-lesson-meta">${esc(meta)}</span>` : ""}
      </span>
    </div>`;
}

function renderDays(): string {
  if (!data) return "";
  const today = isoDate(new Date());

  const days = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    const iso = isoDate(date);
    const lessons = data!.lessons.filter(l => l.date === iso);
    const closure = closureFor(iso, data!.closedDays);

    // Leere Wochenenden verschlucken: sie stehen nur da, wenn Unterricht anliegt.
    if (lessons.length === 0 && i >= 5 && !closure) return "";

    const body = lessons.length > 0
      ? lessons.map(renderLesson).join("")
      : `<div class="st-day-empty">${closure ? esc(closure) : "Kein Unterricht"}</div>`;

    return `
      <section class="st-day${iso === today ? " st-day-today" : ""}">
        <h2 class="st-day-head">
          <span>${esc(WEEKDAY_NAMES[i])}</span>
          <span class="st-day-date">${esc(formatDate(iso))}${closure && lessons.length > 0 ? ` · ${esc(closure)}` : ""}</span>
        </h2>
        ${body}
      </section>`;
  }).join("");

  return days || `<div class="card" style="padding:2rem;text-align:center;color:var(--gray-400)">
    In dieser Woche ist kein Unterricht eingetragen.</div>`;
}

async function exportIcs(btn: HTMLButtonElement) {
  const from = isoDate(mondayOf(new Date()));
  const to = isoDate(addDays(new Date(), EXPORT_DAYS));
  btn.disabled = true;
  try {
    // Nicht die angezeigte Woche exportieren, sondern ein halbes Jahr voraus:
    // eine Kalenderdatei mit fünf Terminen hilft niemandem.
    const range = await api.schuelerApp.stundenplan(from, to);
    if (range.lessons.length === 0) { showToast("Keine Termine zum Exportieren"); return; }

    const blob = new Blob([generateLessonIcs(range.lessons)], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mein-stundenplan.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (err: any) {
    showToast("Fehler: " + err.message);
  } finally {
    btn.disabled = false;
  }
}

function renderBody() {
  const host = currentEl?.querySelector("#st-body") as HTMLElement | null;
  if (!host || !data) return;

  const { week, year } = isoWeek(weekStart);

  host.innerHTML = `
    <div class="st-toolbar">
      <div class="st-week">
        <span class="st-week-kw">KW ${week}</span>
        <span class="st-week-range">${esc(formatDate(weekStart))} bis ${esc(formatDate(weekEnd()))} · ${year}</span>
      </div>
      <div class="st-nav">
        <button class="btn btn-secondary btn-sm st-nav-arrow" id="st-prev" aria-label="Vorherige Woche">‹</button>
        <button class="btn btn-secondary btn-sm" id="st-today">Heute</button>
        <button class="btn btn-secondary btn-sm st-nav-arrow" id="st-next" aria-label="Nächste Woche">›</button>
      </div>
    </div>
    ${renderDays()}`;

  host.querySelector("#st-prev")!.addEventListener("click", () => { weekStart = addDays(weekStart, -7); refresh(); });
  host.querySelector("#st-next")!.addEventListener("click", () => { weekStart = addDays(weekStart, 7); refresh(); });
  host.querySelector("#st-today")!.addEventListener("click", () => { weekStart = mondayOf(new Date()); refresh(); });
}

async function refresh() {
  const host = currentEl?.querySelector("#st-body") as HTMLElement | null;
  if (host) host.innerHTML = `<div style="display:flex;justify-content:center;padding:2rem"><div class="spinner"></div></div>`;
  try {
    data = await api.schuelerApp.stundenplan(isoDate(weekStart), isoDate(weekEnd()));
    renderBody();
  } catch (err: any) {
    if (host) host.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
  }
}

export async function render(el: HTMLElement) {
  currentEl = el;
  weekStart = mondayOf(new Date());

  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  let initial: StudentTimetable;
  try {
    initial = await api.schuelerApp.stundenplan(isoDate(weekStart), isoDate(weekEnd()));
  } catch (err: any) {
    el.innerHTML = `<div class="page-header"><h1>Mein Stundenplan</h1></div>
      <div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }
  data = initial;

  // Ohne Klasse gibt es nichts anzuzeigen, und die Wochennavigation wäre sinnlos.
  if (!initial.className) {
    el.innerHTML = `<div class="page-header"><h1>Mein Stundenplan</h1></div>
      <div class="card" style="padding:2rem;text-align:center;color:var(--gray-400)">
        Du bist noch keiner Klasse zugeordnet. Sobald das erledigt ist, steht dein Stundenplan hier.
      </div>`;
    return;
  }

  el.innerHTML = `
    <div class="page-header">
      <h1>Mein Stundenplan</h1>
      <button class="btn btn-secondary btn-sm" id="st-ics">Kalender exportieren (.ics)</button>
    </div>
    <div style="font-size:.875rem;color:var(--gray-500);margin-bottom:1rem">Klasse ${esc(initial.className)}</div>
    <div id="st-body"></div>`;

  el.querySelector("#st-ics")!.addEventListener("click", (e) => exportIcs(e.currentTarget as HTMLButtonElement));
  renderBody();
}
